import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ThemeProvider, createTheme, CssBaseline, Box, TextField,
  Button, Typography, CircularProgress,
} from "@mui/material";
import api from "../api/api";

const theme = createTheme({
  palette: {
    mode: "dark",
    primary:    { main: "#6c7ef8" },
    background: { default: "#0d0e14", paper: "#13151e" },
    text:       { primary: "#f0f2f7", secondary: "#b0b7ce" },
  },
  typography: { fontFamily: "'DM Sans', system-ui, sans-serif" },
});

const D = {
  bg:           "#0d0e14",
  surface:      "#13151e",
  surfaceUp:    "#191c28",
  border:       "rgba(255,255,255,0.07)",
  borderMed:    "rgba(255,255,255,0.13)",
  accent:       "#6c7ef8",
  accentSoft:   "rgba(108,126,248,0.10)",
  accentBorder: "rgba(108,126,248,0.28)",
  danger:       "#f87171",
  dangerSoft:   "rgba(248,113,113,0.10)",
  text:         "#f0f2f7",
  textSec:      "#b0b7ce",
  textMuted:    "#6b7390",
  fontDisplay:  "'Plus Jakarta Sans','DM Sans',sans-serif",
  font:         "'DM Sans',sans-serif",
};

const G = `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@600;700;800&family=DM+Sans:wght@400;500;600&display=swap');
  *, *::before, *::after { box-sizing: border-box; }
  body { background: ${D.bg}; -webkit-font-smoothing: antialiased; }

  @keyframes fadeUp    { from{opacity:0;transform:translateY(20px)} to{opacity:1;transform:translateY(0)} }
  @keyframes orbFloat1 { 0%,100%{transform:translate(0,0) scale(1)} 50%{transform:translate(30px,-20px) scale(1.05)} }
  @keyframes orbFloat2 { 0%,100%{transform:translate(0,0) scale(1)} 50%{transform:translate(-20px,25px) scale(1.08)} }
  @keyframes glowPulse { 0%,100%{box-shadow:0 0 32px rgba(108,126,248,0.18),0 0 0 1px rgba(108,126,248,0.14)} 50%{box-shadow:0 0 48px rgba(108,126,248,0.28),0 0 0 1px rgba(108,126,248,0.22)} }
  @keyframes logoSpin  { 0%,100%{box-shadow:0 0 18px rgba(108,126,248,0.35)} 50%{box-shadow:0 0 30px rgba(163,123,248,0.5)} }

  .admin-card { animation: fadeUp 0.5s cubic-bezier(0.22,1,0.36,1) both; }

  .orb1 {
    position:fixed; width:560px; height:560px; border-radius:50%; z-index:0; pointer-events:none;
    background: radial-gradient(circle, rgba(108,126,248,0.07) 0%, transparent 70%);
    top:-160px; left:-160px;
    animation: orbFloat1 14s ease-in-out infinite;
  }
  .orb2 {
    position:fixed; width:480px; height:480px; border-radius:50%; z-index:0; pointer-events:none;
    background: radial-gradient(circle, rgba(163,123,248,0.06) 0%, transparent 70%);
    bottom:-140px; right:-100px;
    animation: orbFloat2 18s ease-in-out infinite;
  }
`;

export default function AdminLogin() {
  const navigate = useNavigate();
  const [email,    setEmail]    = useState("");
  const [password, setPassword] = useState("");
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState("");

  async function handleLogin(e) {
    e.preventDefault();
    setError("");
    if (!email.trim() || !password.trim()) {
      setError("Email and password are required.");
      return;
    }
    setLoading(true);
    try {
      const res = await api.post("/admin/login", { email: email.trim().toLowerCase(), password });
      localStorage.setItem("admin_token", res.data.token);
      navigate("/admin");
    } catch (err) {
      setError(err.response?.data?.detail || "Invalid credentials.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <style>{G}</style>
      <div className="orb1" />
      <div className="orb2" />

      <Box sx={{
        minHeight: "100vh",
        background: D.bg,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        px: 2,
        position: "relative",
        zIndex: 1,
      }}>
        {/* Wordmark */}
        <Box sx={{ mb: 4, textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
            <Box sx={{
              width: 32, height: 32, borderRadius: "9px",
              background: `linear-gradient(135deg, ${D.accent} 0%, #a37bf8 100%)`,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontFamily: D.fontDisplay, fontWeight: 800, fontSize: 16, color: "#fff",
              boxShadow: "0 4px 16px rgba(108,126,248,0.38)", animation: "logoGlow 3s ease infinite",
            }}>Z</Box>
            <Typography sx={{ fontFamily: D.fontDisplay, fontWeight: 700, fontSize: 17, color: D.text, letterSpacing: "-0.02em" }}>
              zenithpost<span style={{ color: D.accent }}>.ai</span>
            </Typography>
          </Box>
          <Typography sx={{ fontSize: "11px", fontFamily: "'JetBrains Mono', monospace", color: D.textMuted, letterSpacing: "0.14em", textTransform: "uppercase" }}>
            Admin Console
          </Typography>
        </Box>

        {/* Card */}
        <Box
          component="form"
          onSubmit={handleLogin}
          className="admin-card"
          sx={{
            width: "100%",
            maxWidth: 420,
            background: "rgba(19,21,30,0.85)",
            backdropFilter: "blur(16px)",
            border: `1px solid ${D.border}`,
            borderRadius: "20px",
            p: { xs: "28px 24px", sm: "36px 36px" },
            position: "relative",
            overflow: "hidden",
            animation: "glowPulse 4s ease-in-out infinite",
          }}
        >
          {/* Top accent line */}
          <Box sx={{
            position: "absolute", top: 0, left: "15%", right: "15%", height: "1px",
            background: "linear-gradient(90deg, transparent, rgba(108,126,248,0.5), transparent)",
          }} />

          {/* Shield icon */}
          <Box sx={{
            width: 52, height: 52, borderRadius: "14px",
            background: D.accentSoft,
            border: `1px solid ${D.accentBorder}`,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: "22px", mb: 3, mx: "auto",
            animation: "logoSpin 3s ease-in-out infinite",
          }}>
            🛡️
          </Box>

          <Typography sx={{
            fontFamily: D.fontDisplay,
            fontWeight: 700,
            fontSize: "20px",
            color: D.text,
            letterSpacing: "-0.03em",
            textAlign: "center",
            mb: 0.75,
          }}>
            Sign in to Admin
          </Typography>
          <Typography sx={{
            fontSize: "13px",
            color: D.textMuted,
            textAlign: "center",
            mb: 3.5,
            fontFamily: D.font,
          }}>
            Restricted access — authorised users only
          </Typography>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              label="Admin Email"
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              fullWidth
              autoComplete="email"
              size="small"
              sx={inputSx}
            />
            <TextField
              label="Password"
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              fullWidth
              autoComplete="current-password"
              size="small"
              sx={inputSx}
            />

            {error && (
              <Box sx={{ background: D.dangerSoft, border: "1px solid rgba(248,113,113,0.22)", borderRadius: "10px", px: 2, py: 1.25 }}>
                <Typography sx={{ fontSize: "13px", color: D.danger, fontFamily: D.font }}>
                  {error}
                </Typography>
              </Box>
            )}

            <Button
              type="submit"
              fullWidth
              disabled={loading}
              sx={{
                mt: 0.5,
                py: 1.4,
                borderRadius: "12px",
                background: `linear-gradient(135deg, ${D.accent}, #8b7cf8)`,
                color: "#fff",
                fontFamily: D.fontDisplay,
                fontWeight: 700,
                fontSize: "14.5px",
                textTransform: "none",
                letterSpacing: "0.01em",
                boxShadow: `0 4px 20px rgba(108,126,248,0.25)`,
                transition: "all 0.2s",
                "&:hover": {
                  opacity: 0.92,
                  background: `linear-gradient(135deg, ${D.accent}, #8b7cf8)`,
                  boxShadow: "0 6px 28px rgba(108,126,248,0.35)",
                  transform: "translateY(-1px)",
                },
                "&:disabled": { opacity: 0.45 },
              }}
            >
              {loading ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Sign in →"}
            </Button>
          </Box>
        </Box>

        <Typography sx={{ mt: 3, fontSize: "12px", color: D.textMuted, fontFamily: D.font }}>
          Not an admin?{" "}
          <Box
            component="span"
            onClick={() => navigate("/login")}
            sx={{ color: D.accent, cursor: "pointer", "&:hover": { textDecoration: "underline" } }}
          >
            Go to client login
          </Box>
        </Typography>
      </Box>
    </ThemeProvider>
  );
}

const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    background: "rgba(255,255,255,0.03)",
    fontFamily: "'DM Sans',sans-serif",
    fontSize: "14px",
    transition: "background 0.2s",
    "&:hover": { background: "rgba(255,255,255,0.05)" },
  },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.09)" },
  "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.18)" },
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#6c7ef8", borderWidth: "1.5px" },
  "& .MuiInputLabel-root": { fontFamily: "'DM Sans',sans-serif", fontSize: "14px", color: "#6b7390" },
  "& .MuiInputLabel-root.Mui-focused": { color: "#6c7ef8" },
  "& input": { color: "#f0f2f7" },
};
